// Tells the dynamic /:tenant slug entry apart from every static top-level
// route. The auth gate uses this to let signed-out visitors stay on a tenant
// booking page (guest widget flow) instead of bouncing them to /login.
//
// KEEP IN SYNC with the route table in App.tsx: every static top-level
// <Route> segment must be listed here. A segment missing from this set makes
// the gate treat that route as a tenant slug — a signed-out visit would then
// render the screen (with no data) instead of redirecting to /login.
export const RESERVED_TOP_SEGMENTS = new Set([
  // Public + auth steps
  'login', 'verify', 'mfa', 'reset-password', 'privacy', 'terms', 'tokens', 'auth',
  // Operator
  'chat', 'setup', 'provisioning', 'overview', 'schedule', 'inbox', 'conversation',
  'customers', 'customer', 'team', 'bookings', 'booking', 'payments', 'refunds',
  'new-sale', 'package-sales', 'payment-requests', 'memberships',
  'services', 'service', 'service-categories', 'packages', 'package', 'resources', 'resource', 'hours', 'schedules',
  'time-blocks', 'calendar-notes', 'open-slots', 'templates', 'template', 'reminder-log',
  'notifications', 'notification', 'notification-log', 'sequences', 'sequence', 'sequence-log', 'settings',
  'campaigns', 'campaign', 'email-templates', 'email-template', 'segments', 'segment',
  'intake', 'intake-forms', 'intake-form', 'intake-submissions', 'intake-submission',
  'treatment-cards', 'treatment-card',
  // Customer portal
  'portal',
])

/**
 * The public intake form filler: /intake/:token. Reachable SIGNED OUT by
 * design — the person filling it in is a customer on their own phone with no
 * session of any kind; the unguessable token in the URL is the credential.
 * The AuthGate exempts these paths exactly as it exempts tenant slug pages.
 */
export function isPublicIntakePath(pathname: string): boolean {
  const segments = pathname.split('/').filter(Boolean)
  return segments.length === 2 && segments[0] === 'intake'
}

/** True when the path can only match the /:tenant slug route (e.g. /innoshape). */
export function isTenantSlugPath(pathname: string): boolean {
  const segments = pathname.split('/').filter(Boolean)
  return segments.length === 1 && !RESERVED_TOP_SEGMENTS.has(segments[0])
}

/**
 * The public package shop: /:tenant/buy and /:tenant/buy/:packageId. Reachable
 * SIGNED OUT by design — the whole point of a shared purchase link is that it
 * works for somebody who has never used this business before, from an SMS or a
 * QR code on a card. Nothing in the URL is a credential: it names a package that
 * is already on public sale, and the buyer identifies themselves on the page.
 *
 * Deliberately its OWN predicate rather than a loosening of isTenantSlugPath,
 * which stays strictly one segment. That keeps the rule fail-closed: a future
 * child route under /:tenant is NOT reachable signed-out until it is named here,
 * which is the right default for a route that might show somebody's data.
 */
export function isPublicPurchasePath(pathname: string): boolean {
  const segments = pathname.split('/').filter(Boolean)
  if (segments.length !== 2 && segments.length !== 3) return false
  return segments[1] === 'buy' && !RESERVED_TOP_SEGMENTS.has(segments[0])
}
